import type { RouteRecord } from 'vite-react-ssg'
import Layout from './components/Layout'
import Home from './pages/Home'
import PaintCalculator from './pages/PaintCalculator'
import TileCalculator from './pages/TileCalculator'
import ConcreteCalculator from './pages/ConcreteCalculator'
import MulchCalculator from './pages/MulchCalculator'
import DrywallCalculator from './pages/DrywallCalculator'
import FlooringCalculator from './pages/FlooringCalculator'
import WallpaperCalculator from './pages/WallpaperCalculator'
import GravelCalculator from './pages/GravelCalculator'
import DeckCalculator from './pages/DeckCalculator'
import FenceCalculator from './pages/FenceCalculator'
import RoofingCalculator from './pages/RoofingCalculator'
import InsulationCalculator from './pages/InsulationCalculator'
import RoofPitchCalculator from './pages/RoofPitchCalculator'
import StairCalculator from './pages/StairCalculator'
import SheetCutCalculator from './pages/SheetCutCalculator'
import DimensionsConverter from './pages/DimensionsConverter'
import LumberCalculator from './pages/LumberCalculator'
import CalculatorRoute from './pages/CalculatorRoute'
import CalculatorsIndex from './pages/CalculatorsIndex'
import MySheets from './pages/MySheets'
import ProjectPage from './pages/ProjectPage'
import KanbanBoard from './pages/KanbanBoard'
import Resources from './pages/Resources'
import GuidePaintRoom from './pages/GuidePaintRoom'
import GuideBathroomChecklist from './pages/GuideBathroomChecklist'
import GuideKitchenRemodel from './pages/GuideKitchenRemodel'
import GuideTileInstallation from './pages/GuideTileInstallation'
import GuideDeckMaterials from './pages/GuideDeckMaterials'
import GuideConcreteMixing from './pages/GuideConcreteMixing'
import HowToUse from './pages/HowToUse'
import KanbanBoardHowItWorks from './pages/KanbanBoardHowItWorks'
import ImportPage from './pages/ImportPage'
import { About, Contact, HowWeCalculate, Terms, Privacy, AffiliateDisclosure, NotFound } from './pages/StaticPages'
import { CALCULATORS, CATEGORIES } from './data/calculators'

// NOTE: routes are EAGERLY imported on purpose. A lazy/code-split version was
// tried (smaller homepage JS) but on slow networks the lazy route chunk arrived
// after hydration, so React replaced the pre-rendered content mid-load → a
// full-page reflow that wrecked CLS (0 → 0.65) and LCP. The monolithic bundle
// keeps CWV green; revisit code-splitting only with proper route-chunk preload.
export const routes: RouteRecord[] = [
  {
    path: '/',
    Component: Layout,
    children: [
      { index: true, Component: Home },
      { path: 'calculators', Component: CalculatorsIndex },
      { path: 'calculators/paint', Component: PaintCalculator },
      { path: 'calculators/tile', Component: TileCalculator },
      { path: 'calculators/concrete', Component: ConcreteCalculator },
      { path: 'calculators/mulch', Component: MulchCalculator },
      { path: 'calculators/drywall', Component: DrywallCalculator },
      { path: 'calculators/flooring', Component: FlooringCalculator },
      { path: 'calculators/wallpaper', Component: WallpaperCalculator },
      { path: 'calculators/gravel', Component: GravelCalculator },
      { path: 'calculators/deck', Component: DeckCalculator },
      { path: 'calculators/fence', Component: FenceCalculator },
      { path: 'calculators/roofing', Component: RoofingCalculator },
      { path: 'calculators/insulation', Component: InsulationCalculator },
      { path: 'calculators/roof-pitch', Component: RoofPitchCalculator },
      { path: 'calculators/stair', Component: StairCalculator },
      { path: 'calculators/sheet-cut', Component: SheetCutCalculator },
      { path: 'calculators/dimensions', Component: DimensionsConverter },
      { path: 'calculators/lumber', Component: LumberCalculator },
      {
        path: 'calculators/:slug',
        Component: CalculatorRoute,
        getStaticPaths: () => [
          ...CATEGORIES.map((c) => `/calculators/${c.slug}`),
          ...CALCULATORS.filter((c) => !c.live).map((c) => `/calculators/${c.slug}`),
        ],
      },
      { path: 'my-sheets', Component: MySheets },
      { path: 'my-sheets/project/:id', Component: ProjectPage },
      { path: 'kanban-board', Component: KanbanBoard },
      { path: 'kanban-board/how-it-works', Component: KanbanBoardHowItWorks },
      { path: 'import', Component: ImportPage },
      { path: 'fr/import', Component: ImportPage },
      { path: 'how-to-use', Component: HowToUse },
      { path: 'resources', Component: Resources },
      { path: 'guides', Component: Resources },
      { path: 'guides/how-to-paint-a-room', Component: GuidePaintRoom },
      { path: 'guides/bathroom-renovation-checklist', Component: GuideBathroomChecklist },
      { path: 'guides/kitchen-remodel-planning-guide', Component: GuideKitchenRemodel },
      { path: 'guides/tile-installation-guide', Component: GuideTileInstallation },
      { path: 'guides/deck-building-materials-list', Component: GuideDeckMaterials },
      { path: 'guides/concrete-mixing-and-pouring', Component: GuideConcreteMixing },
      { path: 'about', Component: About },
      { path: 'contact', Component: Contact },
      { path: 'how-we-calculate', Component: HowWeCalculate },
      { path: 'terms', Component: Terms },
      { path: 'privacy', Component: Privacy },
      { path: 'affiliate-disclosure', Component: AffiliateDisclosure },

      // ── French (fr-CA) — pages translated so far. Locale is derived from the /fr path. ──
      { path: 'fr', Component: Home },
      { path: 'fr/kanban-board', Component: KanbanBoard },
      { path: 'fr/calculators/paint', Component: PaintCalculator },
      { path: 'fr/calculators/tile', Component: TileCalculator },
      { path: 'fr/calculators/concrete', Component: ConcreteCalculator },
      { path: 'fr/calculators/mulch', Component: MulchCalculator },
      { path: 'fr/calculators/drywall', Component: DrywallCalculator },
      { path: 'fr/calculators/flooring', Component: FlooringCalculator },
      { path: 'fr/calculators/wallpaper', Component: WallpaperCalculator },
      { path: 'fr/calculators/gravel', Component: GravelCalculator },
      { path: 'fr/calculators/deck', Component: DeckCalculator },
      { path: 'fr/calculators/fence', Component: FenceCalculator },
      { path: 'fr/calculators/roofing', Component: RoofingCalculator },
      { path: 'fr/calculators/insulation', Component: InsulationCalculator },
      { path: 'fr/calculators', Component: CalculatorsIndex },
      { path: 'fr/about', Component: About },
      { path: 'fr/how-to-use', Component: HowToUse },
      { path: 'fr/resources', Component: Resources },
      { path: 'fr/contact', Component: Contact },
      { path: 'fr/how-we-calculate', Component: HowWeCalculate },
      { path: 'fr/terms', Component: Terms },
      { path: 'fr/privacy', Component: Privacy },
      { path: 'fr/affiliate-disclosure', Component: AffiliateDisclosure },
      { path: 'fr/guides/how-to-paint-a-room', Component: GuidePaintRoom },
      { path: 'fr/guides/bathroom-renovation-checklist', Component: GuideBathroomChecklist },
      { path: 'fr/guides/kitchen-remodel-planning-guide', Component: GuideKitchenRemodel },
      { path: 'fr/guides/tile-installation-guide', Component: GuideTileInstallation },
      { path: 'fr/guides/deck-building-materials-list', Component: GuideDeckMaterials },
      { path: 'fr/guides/concrete-mixing-and-pouring', Component: GuideConcreteMixing },
      { path: 'fr/my-sheets', Component: MySheets },
      { path: 'fr/my-sheets/project/:id', Component: ProjectPage },

      { path: '*', Component: NotFound },
    ],
  },
]
